import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { MEMBERS_PAGE_SIZE, type AdminUserStatus, type MemberActivity } from '@ready/shared';
import { useApi, type AdminUser } from '@/lib/api';
import { useMe } from '@/lib/me';
import { formatDate } from '@/lib/dates';
import { useDebounced } from '@/lib/use-debounced';
import { Badge, Button, Card, ErrorText, Input } from '@/components/ui';
import { SearchBox } from '@/components/catalog';
import {
  ACTIVITY_OPTIONS,
  ActivityBadge,
  Filter,
  Pager,
  lastActiveLabel,
  ofTotal,
} from '@/components/people';

type Sort = 'joined' | 'name' | 'active';

/**
 * Everyone on the platform, for platform admins: search, filter by org (with
 * the orgs under it), platform role and activity, sort and page, with each
 * person's engagement across the platform. "Manage" opens a row's actions.
 */
export function AdminUsersPage() {
  const api = useApi();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const [orgId, setOrgId] = useState('');
  const [status, setStatus] = useState<AdminUserStatus | ''>('');
  const [activity, setActivity] = useState<MemberActivity | ''>('');
  const [sort, setSort] = useState<Sort>('joined');
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<string | null>(null);

  const query = {
    q: q || undefined,
    orgId: orgId || undefined,
    status: status || undefined,
    activity: activity || undefined,
    sort,
    offset: page * MEMBERS_PAGE_SIZE,
    limit: MEMBERS_PAGE_SIZE,
  };
  const users = useQuery({
    queryKey: ['admin', 'users', query],
    queryFn: () => api.admin.users(query),
    placeholderData: keepPreviousData,
  });
  const orgs = useQuery({ queryKey: ['admin', 'orgOptions'], queryFn: api.admin.orgOptions });

  /** Any filter change starts again from the first page. */
  const refilter = (apply: () => void) => {
    apply();
    setPage(0);
    setOpen(null);
  };
  const filtered = Boolean(q || orgId || status || activity);
  const toggle = (id: string) => setOpen((o) => (o === id ? null : id));

  return (
    <Card>
      <div className="space-y-3">
        <SearchBox
          value={text}
          onChange={(v) => refilter(() => setText(v))}
          placeholder="Search by name or email"
          label="Search users"
        />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Filter
            id="u-org"
            label="Organization"
            value={orgId}
            onChange={(v) => refilter(() => setOrgId(v))}
            options={[
              ['', 'All organizations'],
              ...(orgs.data?.orgs ?? []).map(
                (o) =>
                  [
                    o.id,
                    `${'\u00a0\u00a0\u00a0'.repeat(o.depth)}${o.depth ? '↳ ' : ''}${o.name}${o.archived ? ' (archived)' : ''}`,
                  ] as [string, string],
              ),
            ]}
          />
          <Filter
            id="u-status"
            label="Type"
            value={status}
            onChange={(v) => refilter(() => setStatus(v as AdminUserStatus | ''))}
            options={[
              ['', 'Everyone'],
              ['admin', 'Platform admins'],
              ['creator', 'Can create organizations'],
              ['suspended', 'Suspended'],
            ]}
          />
          <Filter
            id="u-activity"
            label="Activity"
            value={activity}
            onChange={(v) => refilter(() => setActivity(v as MemberActivity | ''))}
            options={[['', 'Any activity'], ...ACTIVITY_OPTIONS]}
          />
          <Filter
            id="u-sort"
            label="Sort by"
            value={sort}
            onChange={(v) => refilter(() => setSort(v as Sort))}
            options={[
              ['joined', 'Newest'],
              ['name', 'Name'],
              ['active', 'Most recently active'],
            ]}
          />
        </div>
        {orgId && (
          <p className="text-xs text-muted-foreground">
            Includes members of the organizations under it.
          </p>
        )}
      </div>

      <div className="mt-5">
        {users.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : users.isError ? (
          <ErrorText error={users.error} />
        ) : users.data.users.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {filtered ? 'No one matches that search or filters.' : 'No users yet.'}
          </p>
        ) : (
          <UserRows users={users.data.users} open={open} onToggle={toggle} />
        )}
      </div>

      <Pager
        page={page}
        pageSize={MEMBERS_PAGE_SIZE}
        total={users.data?.total ?? 0}
        busy={users.isFetching}
        onPage={(p) => {
          setPage(p);
          setOpen(null);
        }}
      />
    </Card>
  );
}

function UserBadges({ u }: { u: AdminUser }) {
  if (!u.isPlatformAdmin && !u.suspendedAt && !u.creatorGrant) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {u.isPlatformAdmin && <Badge>Platform admin</Badge>}
      {u.suspendedAt && <Badge>Suspended</Badge>}
      {u.creatorGrant && (
        <Badge>
          Can create {u.creatorGrant.maxOrgs} org{u.creatorGrant.maxOrgs === 1 ? '' : 's'}
        </Badge>
      )}
    </div>
  );
}

/** "IIT Hyderabad", "IIT Hyderabad + 2 more", or "—". */
const orgsLabel = (u: AdminUser) =>
  u.orgCount === 0
    ? '—'
    : u.orgCount === 1
      ? u.orgNames[0]
      : `${u.orgNames[0]} + ${u.orgCount - 1} more`;

function ManageButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  const Icon = open ? ChevronUp : ChevronDown;
  return (
    <Button variant="secondary" aria-expanded={open} onClick={onClick}>
      Manage <Icon className="size-4" aria-hidden />
    </Button>
  );
}

function UserRows({
  users,
  open,
  onToggle,
}: {
  users: AdminUser[];
  open: string | null;
  onToggle: (id: string) => void;
}) {
  const name = (u: AdminUser) => (
    <Link to={u.id} className="-my-1 block py-1 hover:text-primary">
      <span className="block break-all font-medium">{u.displayName ?? u.email}</span>
      {u.displayName && (
        <span className="block break-all text-xs text-muted-foreground">{u.email}</span>
      )}
    </Link>
  );
  return (
    <>
      {/* Phones and tablets: one block per person, actions underneath (the table needs room). */}
      <ul className="divide-y divide-border lg:hidden">
        {users.map((u) => (
          <li key={u.id} className="space-y-1.5 py-3 text-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {name(u)}
                <UserBadges u={u} />
              </div>
              <ActivityBadge activity={u.activity} />
            </div>
            <p className="text-xs text-muted-foreground">
              {orgsLabel(u)} · Joined {formatDate(u.createdAt)} · {lastActiveLabel(u.lastActiveAt)}
            </p>
            <p className="text-xs text-muted-foreground">
              Courses {ofTotal(u.coursesCompleted, u.coursesStarted)} · Assessments{' '}
              {ofTotal(u.quizzesPassed, u.quizzesTaken)} · Events {u.eventsAttended} · Certificates{' '}
              {u.certificates}
            </p>
            <div className="pt-1">
              <ManageButton open={open === u.id} onClick={() => onToggle(u.id)} />
            </div>
            {open === u.id && <UserActions u={u} />}
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-2 pr-4 font-medium">User</th>
              <th className="py-2 pr-4 font-medium">Organizations</th>
              <th className="py-2 pr-4 font-medium">Activity</th>
              <th className="py-2 pr-4 font-medium" title="Completed of started">
                Courses
              </th>
              <th className="py-2 pr-4 font-medium" title="Passed of taken">
                Assessments
              </th>
              <th className="py-2 pr-4 font-medium">Events</th>
              <th className="py-2 pr-4 font-medium">Certificates</th>
              <th className="py-2 font-medium">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u) => (
              <UserTableRow
                key={u.id}
                u={u}
                name={name(u)}
                open={open === u.id}
                onToggle={() => onToggle(u.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UserTableRow({
  u,
  name,
  open,
  onToggle,
}: {
  u: AdminUser;
  name: ReactNode;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr className="align-top">
        <td className="min-w-[11rem] py-2.5 pr-4">
          {name}
          <span className="block text-xs text-muted-foreground">
            Joined {formatDate(u.createdAt)}
          </span>
          <UserBadges u={u} />
        </td>
        <td className="max-w-[10rem] py-2.5 pr-4">
          <span className="block truncate" title={u.orgNames.join(', ')}>
            {orgsLabel(u)}
          </span>
        </td>
        <td className="py-2.5 pr-4">
          <ActivityBadge activity={u.activity} />
          <span className="mt-1 block text-xs text-muted-foreground">
            {lastActiveLabel(u.lastActiveAt)}
          </span>
        </td>
        <td className="py-2.5 pr-4 tabular-nums">
          {ofTotal(u.coursesCompleted, u.coursesStarted)}
        </td>
        <td className="py-2.5 pr-4 tabular-nums">{ofTotal(u.quizzesPassed, u.quizzesTaken)}</td>
        <td className="py-2.5 pr-4 tabular-nums">{u.eventsAttended || '—'}</td>
        <td className="py-2.5 pr-4 tabular-nums">{u.certificates || '—'}</td>
        <td className="py-2 text-right">
          <ManageButton open={open} onClick={onToggle} />
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8} className="pb-4">
            <UserActions u={u} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * A person's actions: their name (for certificates), platform admin,
 * suspension, and how many organizations they may create. Every change is
 * audit-logged on the server; platform admins can't change these for themselves.
 */
function UserActions({ u }: { u: AdminUser }) {
  const api = useApi();
  const me = useMe();
  const queryClient = useQueryClient();
  const isSelf = me.data?.id === u.id;
  const [name, setName] = useState(u.displayName ?? '');
  const [maxOrgs, setMaxOrgs] = useState(u.creatorGrant?.maxOrgs.toString() ?? '1');
  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
  const confirmThen = (message: string, fn: () => Promise<unknown>) => {
    if (window.confirm(message)) run.mutate(fn);
  };
  const who = u.displayName ?? u.email;
  const nameChanged = name.trim() !== '' && name.trim() !== (u.displayName ?? '');

  return (
    <div className="mt-2 space-y-4 rounded-[var(--radius)] border border-border bg-muted/40 p-4 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium">Manage {who}</p>
        <Link to={u.id} className="-my-2 py-2 text-primary hover:underline">
          View profile
        </Link>
      </div>

      <form
        className="space-y-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (nameChanged) run.mutate(() => api.admin.setDisplayName(u.id, name.trim()));
        }}
      >
        <label htmlFor={`name-${u.id}`} className="block font-medium">
          Name on certificates
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            id={`name-${u.id}`}
            className="sm:max-w-xs"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="No name yet"
          />
          <Button type="submit" variant="secondary" disabled={!nameChanged || run.isPending}>
            Save name
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Also updates their existing certificates. Learners can correct their name only once.
        </p>
      </form>

      <div className="space-y-1.5">
        <label htmlFor={`orgs-${u.id}`} className="block font-medium">
          Organizations they may create
        </label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            id={`orgs-${u.id}`}
            type="number"
            min={1}
            className="sm:w-24"
            value={maxOrgs}
            onChange={(e) => setMaxOrgs(e.target.value)}
          />
          <Button
            variant="secondary"
            disabled={run.isPending || !(Number(maxOrgs) >= 1)}
            onClick={() =>
              run.mutate(() => api.admin.setCreatorGrant(u.id, { maxOrgs: Number(maxOrgs) }))
            }
          >
            {u.creatorGrant ? 'Update quota' : 'Allow creating organizations'}
          </Button>
          {u.creatorGrant && (
            <Button
              variant="ghost"
              disabled={run.isPending}
              onClick={() => run.mutate(() => api.admin.revokeCreatorGrant(u.id))}
            >
              Revoke
            </Button>
          )}
        </div>
      </div>

      {isSelf ? (
        <p className="text-xs text-muted-foreground">
          You can&rsquo;t change your own admin role or suspend yourself.
        </p>
      ) : (
        <div className="flex flex-col gap-2 border-t border-border pt-4 sm:flex-row">
          <Button
            variant="secondary"
            disabled={run.isPending}
            onClick={() =>
              confirmThen(
                u.isPlatformAdmin
                  ? `Remove ${who} as a platform admin?`
                  : `Make ${who} a platform admin? They'll be able to manage every organization, user and the library.`,
                () => api.admin.setPlatformAdmin(u.id, !u.isPlatformAdmin),
              )
            }
          >
            {u.isPlatformAdmin ? 'Remove platform admin' : 'Make platform admin'}
          </Button>
          <Button
            variant={u.suspendedAt ? 'secondary' : 'ghost'}
            className={u.suspendedAt ? undefined : 'text-destructive'}
            disabled={run.isPending}
            onClick={() =>
              confirmThen(
                u.suspendedAt
                  ? `Restore ${who}'s access?`
                  : `Suspend ${who}? They won't be able to use Nirvik Ready until you restore access.`,
                () => api.admin.setSuspended(u.id, !u.suspendedAt),
              )
            }
          >
            {u.suspendedAt ? 'Restore access' : 'Suspend'}
          </Button>
        </div>
      )}
      <ErrorText error={run.error} />
    </div>
  );
}
